<template>
  <div
    ref="optionDropzone"
    class="option-item"
    @drop.prevent="loadFiles"
    @dragover.prevent="dragOver"
    @dragleave.prevent="dragLeave"
  >
    <div class="option-item__title">
      <div class="option-item__title-main">
        <StitchDropdown
          v-if="canEdit"
          ref="optionMenuDropdown"
          :dropdown-items="dropdownItems"
          :icon-type="ICON_TYPE.MENU"
          :placement="DROPDOWN_PLACEMENT.BOTTOM_START"
        />
        <ElementPopover
          v-if="canEdit"
          v-model="showAssetBrowser"
          :append-to-body="false"
          :visible-arrow="false"
          placement="bottom-end"
          popper-class="option-item__asset-browser"
          trigger="manual"
          width="300"
        >
          <StitchAssetBrowser
            v-if="showAssetBrowser"
            ref="assetBrowser"
            v-click-outside="closeAssetBrowser"
            @select-files="addImagesFromRenders"
          />
        </ElementPopover>
        <h2 class="option-item__name">
          {{ option.name }}
        </h2>
      </div>
    </div>
    <ViewImageSlider
      :disabled="canEdit"
      :images="option.images"
    >
      <template #slide="{ imageIndex }">
        <div class="image-list__image">
          <img
            :alt="`Colorway ${option.name} image ${imageIndex}`"
            crossorigin="use-credentials"
            :src="
              s3trim(option.images[imageIndex].fileUrl, resolutionThumbnail)
            "
            :title="getImageFileName(option.images[imageIndex])"
            @click="openImageSlideshow(option.images, imageIndex)"
          >
          <template v-if="option.images[imageIndex].viewName !== turntableView">
            <template v-if="isMainVersion">
              <i
                v-if="option.images[imageIndex].isThumbnail"
                class="el-icon-arrow-up image-icon image-icon--is-thumbnail"
              />
              <i
                v-else-if="canEdit"
                class="el-icon-arrow-up image-icon image-icon--assign-thumbnail"
                @click="setStyleThumbnail(imageIndex)"
              />
              <i
                v-if="canEdit"
                class="el-icon-close image-icon image-icon--delete"
                @click.once="deleteImage(option.images[imageIndex].id)"
              />
            </template>
            <i
              class="el-icon-download image-icon image-icon--download"
              @click="handleImageDownload(imageIndex)"
            />
          </template>
        </div>
      </template>
    </ViewImageSlider>

    <OptionSlideshow
      v-if="showImageSlideshow"
      :active-index="imageSlideshowActiveIndex"
      :image-urls="imageSlideshowUrls"
      :viewer-image-urls="slideshowImages"
      @close="closeImageSlideshow"
      @slide-change="handleSlideshowChange"
    />
  </div>
</template>

<script>
import VueTypes from 'vue-types'
import { BlockOptionShape, BlockVersionShape } from '@/types'
import { mapGetters } from 'vuex'
import OptionSlideshow from '../../OptionSlideshow'
import ViewImageSlider from './ViewImageSlider'

// s3Trim, triggerDownload
import { DataUtils } from '@/mixins/utils'
import Utils from '@/services/utils'
import {
  IMAGE_EXTENSIONS,
  IMAGE_RESOLUTION,
  IMAGE_VIEW_ANGLE
} from '@/constants/image'
import { DROPDOWN_PLACEMENT, ICON_TYPE } from '@/constants/dropdownOptions'
import { LIBRARY_TYPE } from '@/constants/libraryType'

export default {
  name: 'ViewItem',

  components: {
    OptionSlideshow,
    ViewImageSlider
  },

  mixins: [DataUtils],

  props: {
    option: BlockOptionShape,
    version: BlockVersionShape.loose,
    canEdit: VueTypes.bool.def(false)
  },

  emits: ['add-images', 'update-image', 'update-option', 'delete-option'],

  data () {
    return {
      thumbnailExtensions: IMAGE_EXTENSIONS.map(extension => `.${extension}`),
      resolutionThumbnail: IMAGE_RESOLUTION.THUMBNAIL,
      turntableView: IMAGE_VIEW_ANGLE.VIEW360,
      imageSlideshowUrls: null,
      imageSlideshowActiveIndex: null,
      manageOptionsDisabledMessages: {
        DELETE: [
          'This version comes from a .BW file 🙁 Delete a colorway',
          'on the 3D file and upload it as a new version.'
        ]
      },
      showAssetBrowser: false,
      ICON_TYPE,
      thumbnailId: null,
      DROPDOWN_PLACEMENT
    }
  },

  computed: {
    ...mapGetters(['getItemDetail']),

    /**
     * @returns {object}
     */
    item () {
      return this.getItemDetail({ libraryType: this.libraryType })
    },

    /**
     * @returns {string}
     */
    libraryType () {
      return LIBRARY_TYPE.STYLE
    },

    /**
     * @returns {number|null}
     */
    optionId () {
      return this.option.id || null
    },

    /**
     * @returns {boolean}
     */
    isOptionEmpty () {
      return this.option.images.length === 0
    },

    /**
     * @returns {object[]}
     */
    dropdownItems () {
      return [
        {
          show: this.canEdit && !this.isOptionEmpty,
          name: 'Download All Images',
          method: this.handleAllImagesDownload
        }
      ]
    },

    /**
     * @returns {boolean}
     */
    showImageSlideshow () {
      return this.imageSlideshowUrls !== null
    },

    /**
     * @returns {string[]}
     */
    slideshowImages () {
      return this.option.slideshowImages.map(img => img.file_url).reverse()
    },

    /**
     * @returns {boolean}
     */
    isMainVersion () {
      return this.version.isMain
    }
  },

  methods: {
    /**
     */
    openAssetBrowser () {
      this.showAssetBrowser = true
    },

    /**
     */
    closeAssetBrowser () {
      this.showAssetBrowser = false
    },

    /**
     * @param {object} payload
     */
    emitAddStyleOptionImages (payload) {
      this.$emit('add-images', payload)
    },

    /**
     * @param {string[]} imageUrls
     */
    addImagesFromRenders (imageUrls) {
      if (imageUrls.length > 0) {
        this.emitAddStyleOptionImages({
          styleId: this.item.id,
          versionId: this.version.id,
          optionId: this.option.id,
          urls: imageUrls
        })
      }

      this.closeAssetBrowser()
    },

    /**
     * @param {File[]} files
     */
    includeUploadedFiles (files) {
      this.emitAddStyleOptionImages({
        styleId: this.item.id,
        versionId: this.version.id,
        optionId: this.option.id,
        files
      })
    },

    /**
     */
    addImagesFromFileSystem () {
      Utils.fileUploader({
        extensions: this.thumbnailExtensions,
        callback: this.includeUploadedFiles
      })
    },

    /**
     * @param {number} imageIndex
     */
    setStyleThumbnail (imageIndex) {
      const thumbnailId = this.option.images[imageIndex].id

      this.$emit('update-image', {
        imageId: thumbnailId,
        versionId: this.version.id,
        blockId: this.version.blockId,
        isThumbnail: true
      })

      this.thumbnailId = thumbnailId
    },

    /**
     * @param {number} imageId
     */
    deleteImage (imageId) {
      this.$emit('delete-image', {
        imageId: imageId,
        versionId: this.version.id,
        blockId: this.version.blockId
      })
    },

    /**
     */
    deleteOption () {
      const { id: optionId, name: optionName } = this.option

      this.$confirm(
        `This will permanently delete ${optionName}. Continue?`,
        'Warning',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
          showClose: false
        }
      )
        .then(() => {
          this.$emit('delete-option', {
            optionId,
            optionName
          })
        })
        .catch(() => {})
    },

    /**
     * @param   {string} image
     *
     * @returns {string}
     */
    getImageFileName (image) {
      if (image.viewName === IMAGE_VIEW_ANGLE.VIEW360) {
        return '360-placeholder-image'
      }

      return image.file.split('/').pop().split('.')[0]
    },

    /**
     * @param {Event} event
     */
    loadFiles (event) {
      if (!this.canEdit) {
        return
      }

      this.$refs.optionDropzone.classList.remove('dragging')

      const validFiles = Utils.getValidFilesFromEvent(
        event,
        this.thumbnailExtensions
      )

      if (validFiles.length === 0) {
        return
      }

      this.emitAddStyleOptionImages({
        styleId: this.item.id,
        versionId: this.version.id,
        optionId: this.option.id,
        files: validFiles
      })
    },

    /**
     */
    dragOver () {
      if (!this.canEdit || this.isSorting) {
        return
      }

      this.$refs.optionDropzone.classList.add('dragging')
    },

    /**
     */
    dragLeave () {
      if (!this.canEdit) {
        return
      }

      this.$refs.optionDropzone.classList.remove('dragging')
    },

    /**
     * @param {object[]} images
     * @param {number}   imageIndex
     */
    openImageSlideshow (images, imageIndex) {
      this.imageSlideshowUrls = images.map(image => image.fileUrl)
      this.imageSlideshowActiveIndex = imageIndex
    },

    /**
     */
    closeImageSlideshow () {
      this.imageSlideshowUrls = null
      this.imageSlideshowActiveIndex = null
    },

    /**
     * @param {number} imageIndex
     */
    handleSlideshowChange (imageIndex) {
      // TODO: Events Tracking
    },

    /**
     * @param {number} imageIndex
     */
    handleImageDownload (imageIndex) {
      this.triggerDownload(this.option.images[imageIndex].fileUrl)
    },

    /**
     */
    handleAllImagesDownload () {
      this.triggerDownload(this.option.zipUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
$image-icon-size: spacing(2);
$option-image-icon-z-index-hover: 1;
$option-image-opacity: 0.4;
$image-loading-background-size: 70%;
$option-image-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

.option-item__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.option-item__title-main {
  display: flex;
  align-items: center;
  line-height: 1;

  > * {
    margin-right: spacing(1);
  }

  /deep/ .el-dropdown {
    display: flex;
  }

  /deep/ .el-button {
    padding: 0;
  }
}

.option-item__tags {
  display: flex;
  flex-wrap: wrap;
  gap: spacing(1);
  max-width: 100%;
}

.image-icon {
  @include flex-center;

  position: absolute;
  width: $image-icon-size;
  height: $image-icon-size;
  color: $blue;
  font-weight: $font-weight-bold;
  line-height: 1;
  text-align: center;
  opacity: 0;
  transition: opacity $transition;
}

.image-icon--loader {
  top: 0;
}

.image-icon--assign-thumbnail,
.image-icon--is-thumbnail {
  top: spacing(1/2);
  left: spacing(1/2);
}

.image-icon--is-thumbnail {
  align-items: start;
  color: $white;
  background: $blue;
  border-radius: $border-radius-round;
  opacity: 1;

  &:hover {
    opacity: 1;
  }
}

.image-icon--delete {
  top: spacing(1/2);
  right: spacing(1/2);
  color: $red;
}

.image-icon--download {
  right: spacing(1/2);
  bottom: spacing(1/2);
}

.image-list__image {
  width: 100%;
  height: 100%;

  .image-icon:hover {
    z-index: $option-image-icon-z-index-hover;
    opacity: $option-image-opacity;
  }

  .image-loading {
    position: absolute;
    top: 0;
    z-index: $z-index-layer-2;
    width: 100%;
    height: 100%;
    background:
      url('~@/assets/images/img-placeholder.svg') no-repeat center
      center;
    background-size: $image-loading-background-size;
    border-radius: $border-radius-s;
  }

  &:hover {
    box-shadow: $option-image-shadow;

    > img {
      opacity: $option-image-opacity;
    }

    .image-icon {
      opacity: 1;
    }

    .image-icon--assign-thumbnail:hover,
    .image-icon--delete:hover,
    .image-icon--download:hover {
      opacity: $option-image-opacity;
    }
  }
}

.dragging {
  background-color: $blue-light;
}
</style>

<style lang="scss">
.option-item__asset-browser {
  padding: 0;
}
</style>
